import React, { useState } from 'react';
import { X, MessageSquare, Video, MessageCircle, Mail, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CustomChat } from './CustomChat';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from '@/components/ui/sheet';

interface FAQItem {
  question: string;
  answer: string;
}

interface HelpSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  faqs?: FAQItem[];
  videoUrl?: string;
  videoTitle?: string;
}

export const HelpSidebar: React.FC<HelpSidebarProps> = ({
  isOpen = false,
  onClose,
  faqs = [],
  videoUrl = 'https://www.youtube.com/embed/dQw4w9WgXcQ', // Default placeholder video
  videoTitle = 'Help Video'
}) => {
  const [activeTab, setActiveTab] = useState<'faq' | 'video' | 'chat'>('faq');

  // Default FAQs if none provided
  const defaultFAQs: FAQItem[] = [
    {
      question: 'What is a Management System?',
      answer: 'A management system is a set of policies, processes, and procedures used by an organization to ensure it can fulfill all the tasks required to achieve its objectives.'
    },
    {
      question: 'Why should I do this now?',
      answer: 'Completing this step early ensures your compliance framework is set up correctly from the start, saving time and reducing risks later.'
    },
    {
      question: 'Can I change this later?',
      answer: 'Yes, all information can be updated at any time through the settings menu.'
    },
    {
      question: 'Can I skip this step?',
      answer: 'While you can skip this step, we recommend completing it now to ensure full compliance with the standard.'
    },
    {
      question: 'I don\'t know an answer—should I stop and come back?',
      answer: 'No need to stop! You can save your progress and return later. Your answers can be updated at any time.'
    }
  ];

  const displayFAQs = faqs.length > 0 ? faqs : defaultFAQs;

  return (
    <Sheet open={isOpen} modal={false}>
      <SheetContent hideOverlay side="right" className="w-full sm:w-[32rem] p-0 flex flex-col cancelDrag bg-card/95 backdrop-blur-xl border-l border-white/10" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <SheetHeader className="border-b border-white/10 bg-gradient-to-b from-primary/5 to-transparent p-6">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-2xl font-bold text-foreground">Help & Support</SheetTitle>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 hover:bg-white/10" 
              aria-label="Close help"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </SheetHeader>

      <div className="flex gap-2 p-4 bg-gradient-to-b from-white/5 to-transparent backdrop-blur-sm">
        <Button
          variant="ghost"
          className={`flex-1 rounded-xl py-3 transition-all duration-200 ${activeTab === 'faq' ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:bg-primary/90' : 'text-muted-foreground hover:text-foreground hover:bg-white/10 border border-white/10'}`}
          onClick={() => setActiveTab('faq')}
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          FAQs
        </Button>
        <Button
          variant="ghost"
          className={`flex-1 rounded-xl py-3 transition-all duration-200 ${activeTab === 'video' ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:bg-primary/90' : 'text-muted-foreground hover:text-foreground hover:bg-white/10 border border-white/10'}`}
          onClick={() => setActiveTab('video')}
        >
          <Video className="h-4 w-4 mr-2" />
          Videos
        </Button>
        <Button
          variant="ghost"
          className={`flex-1 rounded-xl py-3 transition-all duration-200 ${activeTab === 'chat' ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:bg-primary/90' : 'text-muted-foreground hover:text-foreground hover:bg-white/10 border border-white/10'}`}
          onClick={() => setActiveTab('chat')}
        >
          <MessageCircle className="h-4 w-4 mr-2" />
          Chat
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'faq' && (
          <Accordion type="single" collapsible className="w-full">
            {displayFAQs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-muted-foreground whitespace-pre-line">{faq.answer}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}

        {activeTab === 'video' && (
          <div className="space-y-4">
            <div className="aspect-video rounded-xl overflow-hidden border border-white/20 shadow-lg">
              <iframe
                width="100%"
                height="100%"
                src={videoUrl}
                title={videoTitle}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-4">
              <h4 className="font-medium text-foreground mb-2">{videoTitle}</h4>
              <p className="text-sm text-muted-foreground">
                Watch this video for a detailed walkthrough of the current step.
              </p>
            </div>
          </div>
        )}

        {activeTab === 'chat' && (
          <CustomChat />
        )}
      </div>
    </SheetContent>
    </Sheet>
  );
};

export default HelpSidebar;
